import ListIcon from './ListIcon';
import { Building2Icon, SendIcon } from 'assets/icons';
import Badge from 'components/badge/badge';
import CardIconButton from 'components/card/CardIconButton';
import PanelCard, { SECTION_TITLE_CLASS } from 'components/card/PanelCard';
import { NOTIFICATION_TYPES, withPerimeterFallback } from 'components/perimeter/memberNotifications';
import { isUnrestrictedRole } from 'components/perimeter/perimeterMembers';
import { perimeterText } from 'components/perimeter/perimeterText';
import { SkeletonLine } from 'components/skeleton/Skeleton';
import useCompanyPerimeters from 'components/SlideOver/dataSlideOver/data/slideOverCompany/useCompanyPerimeters';
import useMemberNotifications from 'components/SlideOver/dataSlideOver/data/slideOverCompany/useMemberNotifications';
import { companyRoleLine } from 'constants/companyRoles';
import UseContext from 'hooks/useContext';
import useRecordChanged from 'hooks/useRecordChanged';
import { RecordField } from './RecordFields';
import resendInvitation from './resendInvitation';

/**
 * A company of a member, on their contact page: their role there, the
 * perimeters that limit what they see and what they are notified of. The
 * pencil (`onEdit`) opens the company's « Modifier le contact » on them,
 * which changes all three; an invitation still waiting is sent again from here.
 * `actions`: the page's own buttons on the company (remove them from it).
 * `deactivatedLabel`: the badge text of a company where the account is deactivated.
 */
const MemberCompanyCard = ({ companyId, companyName, memberId, email, role, pending, deactivatedLabel, onEdit, actions }) => {
  const { language } = UseContext();
  const perimeters = useCompanyPerimeters(companyId);
  const notifications = useMemberNotifications(companyId, memberId);
  // The edit screen saved: the perimeters and the notifications may have changed with the rest.
  useRecordChanged(() => {
    perimeters.reload();
    notifications.reload();
  });
  const t = (key) => perimeterText(language, key);
  // No route gives a member's perimeters: they are the active ones that list them.
  const own = perimeters.active.filter((perimeter) =>
    (perimeter.members || []).some((entry) => String(entry.id) === String(memberId)),
  );

  const perimetersValue = () => {
    if (isUnrestrictedRole(role)) return t('member.unrestricted');
    if (perimeters.status === 'loading') return <SkeletonLine className="w-40" />;
    if (perimeters.status !== 'ready') return t('member.loadError');
    return own.length ? own.map((perimeter) => perimeter.name).join(', ') : t('member.none');
  };

  // The subjects they are told of, « (périmètre) » when only within theirs. A
  // Director or an Administrator is told of everything the api reads as 'all'.
  const notificationsValue = () => {
    if (notifications.status === 'loading') return <SkeletonLine className="w-48" />;
    if (notifications.status !== 'ready') return 'Les notifications n’ont pas pu être lues.';
    const levels = isUnrestrictedRole(role) ? withPerimeterFallback(notifications.levels) : notifications.levels;
    const label = (type) => language.getValue(`companyContacts.edit.notifications.types.${type}.label`, type);
    const told = NOTIFICATION_TYPES.filter((type) => levels[type] !== 'none').map((type) =>
      levels[type] === 'perimeter' ? `${label(type)} (périmètre)` : label(type),
    );
    return told.length ? told.join(', ') : 'Aucune';
  };

  return (
    <PanelCard
      icon={<ListIcon icon={Building2Icon} />}
      plainIcon
      title={companyName}
      titleClassName={SECTION_TITLE_CLASS}
      subtitle={companyRoleLine(language, role)}
      badge={
        <>
          {deactivatedLabel && <Badge type="OUTLINE">{deactivatedLabel}</Badge>}
          {pending && (
            <>
              <Badge type="OUTLINE">Invitation en attente</Badge>
              <CardIconButton
                label="Renvoyer l’invitation"
                icon={<SendIcon wh={18} color="#374151" />}
                onClick={() => resendInvitation({ companyId, memberId, email })}
              />
            </>
          )}
          {actions}
        </>
      }
      onEdit={onEdit}
    >
      <div className="px-4">
        <RecordField label="Périmètres">{perimetersValue()}</RecordField>
        <RecordField label="Notifications">{notificationsValue()}</RecordField>
      </div>
    </PanelCard>
  );
};

// A company of a contact without an account, in the same frame: their role,
// perimeters and notifications only come with the account. `actions`: the
// page's own buttons on the company (invite, remove them from it).
export const ContactCompanyCard = ({ companyName, deactivatedLabel, actions }) => (
  <PanelCard
    icon={<ListIcon icon={Building2Icon} />}
    plainIcon
    title={companyName}
    titleClassName={SECTION_TITLE_CLASS}
    subtitle="Non inscrit sur Jobbiz"
    badge={
      <>
        {deactivatedLabel && <Badge type="OUTLINE">{deactivatedLabel}</Badge>}
        {actions}
      </>
    }
  />
);

export default MemberCompanyCard;
